import React, { createContext, useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useCanvasShapes } from '../hooks/useCanvasShapes';
import { DEFAULT_FILL, DEFAULT_SHAPE_SIZE } from '../utils/constants';
import { createShape, updateShape as updateShapeInFirestore, deleteShape as deleteShapeFromFirestore, deleteAllShapes as deleteAllShapesFromFirestore } from '../services/canvas';

// Create the CanvasContext
const CanvasContext = createContext();

// CanvasProvider component
export function CanvasProvider({ children }) {
  // Get current user for shape operations
  const { currentUser } = useAuth();
  
  // Get shapes from Firestore with real-time updates
  const { shapes, loading: shapesLoading } = useCanvasShapes();
  
  // Local canvas state
  const [selectedIds, setSelectedIds] = useState([]); // Multi-select support
  const [previewShapes, setPreviewShapes] = useState([]); // AI preview shapes
  
  // Optimized loading states - only show for significant operations
  const [loadingStates, setLoadingStates] = useState({
    initialLoad: true,           // Show on first canvas load
    creation: false,             // Show only for shape creation
    bulkOperations: false        // Show for delete all, group operations
  });
  
  // Stage reference for Konva
  const stageRef = useRef(null);

  // Sync initialLoad with shapesLoading from Firebase
  useEffect(() => {
    setLoadingStates(prev => ({
      ...prev,
      initialLoad: shapesLoading
    }));
  }, [shapesLoading]);

  // Emergency timeout to prevent infinite loading (failsafe)
  useEffect(() => {
    const timeout = setTimeout(() => {
      console.warn('⚠️ Emergency timeout triggered - forcing loading to complete');
      setLoadingStates(prev => ({
        ...prev,
        initialLoad: false
      }));
    }, 5000); // 5 second max loading time
    
    return () => clearTimeout(timeout);
  }, []);

  // Helper to set specific loading type
  const setLoading = (type, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [type]: isLoading
    }));
  };

  // Only show loading for significant operations (not updates/moves)
  const loading = loadingStates.initialLoad || loadingStates.bulkOperations;
  
  // Backward compatibility - single selection
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;

  // Function to get center of viewport for new shapes
  const getViewportCenter = () => {
    if (!stageRef.current) {
      return { x: 250, y: 250 }; // Default fallback
    }

    const stage = stageRef.current;
    const stageAttrs = stage.attrs;
    const scale = stageAttrs.scaleX || 1;
    const stagePos = stage.position();
    
    // Calculate the center of the visible viewport in canvas coordinates
    const viewportWidth = stage.width() || window.innerWidth;
    const viewportHeight = stage.height() || (window.innerHeight - 64); // Account for navbar
    
    const centerX = (-stagePos.x + viewportWidth / 2) / scale;
    const centerY = (-stagePos.y + viewportHeight / 2) / scale;
    
    return { x: centerX, y: centerY };
  };

  // Generate unique ID for shapes
  const generateShapeId = () => {
    // Use crypto.randomUUID() if available, otherwise fallback to timestamp + random
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `shape_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Shape management methods
  const addShape = async (shapeData = {}) => {
    if (!currentUser) {
      console.error('Cannot add shape: No authenticated user');
      return null;
    }

    try {
      setLoading('creation', true);
      
      // Generate unique ID
      const id = generateShapeId();
      
      // Get center position if not provided
      const center = shapeData.x !== undefined && shapeData.y !== undefined 
        ? { x: shapeData.x, y: shapeData.y } 
        : getViewportCenter();
      
      // Get shape type (default to rectangle)
      const type = shapeData.type || 'rectangle';
      
      console.log('🎨 Creating shape of type:', type, 'at position:', center);
      
      // Create shape object with type-specific defaults
      let newShape = {
        id,
        type,
        x: center.x,
        y: center.y
      };
      
      // Add type-specific properties
      switch (type) {
        case 'rectangle':
          newShape = {
            ...newShape,
            width: shapeData.width || DEFAULT_SHAPE_SIZE.width,
            height: shapeData.height || DEFAULT_SHAPE_SIZE.height,
            fill: shapeData.fill || DEFAULT_FILL
          };
          break;
        case 'circle':
          newShape = {
            ...newShape,
            radius: shapeData.radius || 50,
            fill: shapeData.fill || DEFAULT_FILL
          };
          break;
        case 'text':
          newShape = {
            ...newShape,
            text: shapeData.text !== undefined ? shapeData.text : '', // Start empty if not provided
            fontSize: shapeData.fontSize || 18,
            fill: shapeData.fill || '#000000',
            fontFamily: shapeData.fontFamily || 'Arial',
            isEditing: shapeData.text === undefined || shapeData.text === '' // Start in edit mode if empty
          };
          break;
        case 'line':
          newShape = {
            ...newShape,
            points: shapeData.points || [0, 0, 100, 0], // Horizontal line by default
            stroke: shapeData.stroke || '#000000',
            strokeWidth: shapeData.strokeWidth || 2,
            // Store line position at origin, points are relative to this position
            x: shapeData.x !== undefined ? shapeData.x : center.x,
            y: shapeData.y !== undefined ? shapeData.y : center.y
          };
          break;
        default:
          // Fallback to rectangle
          newShape = {
            ...newShape,
            width: DEFAULT_SHAPE_SIZE.width,
            height: DEFAULT_SHAPE_SIZE.height,
            fill: DEFAULT_FILL
          };
      }

      // Create shape in Firestore (will trigger real-time update)
      await createShape(newShape, currentUser.uid);
      
      // Auto-select the newly created shape
      setSelectedIds([id]);

      console.log('✅ Shape created in Firestore:', type, id);
      return newShape;
    } catch (error) {
      console.error('Error adding shape:', error);
      return null;
    } finally {
      setLoading('creation', false);
    }
  };

  // Batch add AI-generated shapes with metadata
  const addAIShapes = async (aiShapes, aiPrompt = null) => {
    if (!currentUser) {
      console.error('Cannot add AI shapes: No authenticated user');
      return { success: false, addedShapes: [] };
    }

    if (!Array.isArray(aiShapes) || aiShapes.length === 0) {
      console.warn('No shapes to add');
      return { success: false, addedShapes: [] };
    }

    try {
      setLoading('creation', true);
      console.log(`🤖 Adding ${aiShapes.length} AI-generated shapes...`);
      
      const addedShapes = [];
      const failedShapes = [];

      // Add each shape to Firestore with AI metadata
      for (const shapeData of aiShapes) {
        try {
          const id = generateShapeId();
          
          // Prepare shape with AI metadata
          const newShape = {
            ...shapeData,
            id,
            createdByAI: true,
            aiGeneratedBy: currentUser.uid,
            aiGeneratedByName: currentUser.displayName || currentUser.email,
            aiGeneratedAt: Date.now(),
            aiPrompt: aiPrompt || 'AI-generated'
          };

          // Create shape in Firestore
          await createShape(newShape, currentUser.uid);
          addedShapes.push(newShape);
          
          console.log(`✅ Added AI shape: ${newShape.type} (${id.slice(0, 8)})`);
        } catch (error) {
          console.error('Failed to add AI shape:', error);
          failedShapes.push(shapeData);
        }
      }

      // Show success message
      if (addedShapes.length > 0) {
        console.log(`🎉 Successfully added ${addedShapes.length}/${aiShapes.length} AI shapes`);
      }

      if (failedShapes.length > 0) {
        console.warn(`⚠️ Failed to add ${failedShapes.length} shapes`);
      }

      return {
        success: addedShapes.length > 0,
        addedShapes,
        failedShapes,
        total: aiShapes.length,
        successCount: addedShapes.length,
      };
    } catch (error) {
      console.error('Error in batch add AI shapes:', error);
      return { success: false, addedShapes: [], error: error.message };
    } finally {
      setLoading('creation', false);
    }
  };

  const updateShape = async (shapeId, updates) => {
    if (!currentUser) {
      console.error('Cannot update shape: No authenticated user');
      return false;
    }

    try {
      // NO loading state for updates - they should feel instant
      // Update shape in Firestore (will trigger real-time update)
      await updateShapeInFirestore(shapeId, updates, currentUser.uid);

      console.log('Shape updated in Firestore:', shapeId, updates);
      return true;
    } catch (error) {
      console.error('Error updating shape:', error);
      return false;
    }
  };

  const deleteShape = async (shapeId) => {
    if (!currentUser) {
      console.error('Cannot delete shape: No authenticated user');
      return false;
    }

    try {
      // NO loading state for single delete - should feel instant
      
      // Find the shape to check if it's locked
      const shapeToDelete = shapes.find(shape => shape.id === shapeId);
      
      if (!shapeToDelete) {
        console.warn('Shape not found:', shapeId);
        return false;
      }
      
      // Cannot delete if shape is locked by another user
      if (shapeToDelete.isLocked && shapeToDelete.lockedBy !== currentUser.uid) {
        console.warn('Cannot delete shape locked by another user:', shapeToDelete.lockedBy);
        return false;
      }
      
      // Delete from Firestore (will trigger real-time update)
      await deleteShapeFromFirestore(shapeId);
      
      // Clear selection if deleted shape was selected
      if (selectedIds.includes(shapeId)) {
        setSelectedIds(prev => prev.filter(id => id !== shapeId));
      }

      console.log('Shape deleted from Firestore:', shapeId);
      return true;
    } catch (error) {
      console.error('Error deleting shape:', error);
      return false;
    }
  };

  const deleteAllShapes = async () => {
    if (!currentUser) {
      console.error('Cannot delete all shapes: No authenticated user');
      return false;
    }

    try {
      setLoading('bulkOperations', true);

      // Delete all shapes from Firestore (will trigger real-time update)
      const result = await deleteAllShapesFromFirestore();
      
      // Clear local selection
      setSelectedIds([]);

      console.log(`All shapes deleted from Firestore (${result.deletedCount} shapes)`);
      return true;
    } catch (error) {
      console.error('Error deleting all shapes:', error);
      return false;
    } finally {
      setLoading('bulkOperations', false);
    }
  };

  // Helper methods for canvas operations
  const clearSelection = () => {
    setSelectedIds([]);
  };

  const selectShape = (shapeId) => {
    setSelectedIds([shapeId]);
  };
  
  const selectMultiple = (shapeIds) => {
    setSelectedIds(shapeIds);
  };
  
  const toggleSelection = (shapeId) => {
    setSelectedIds(prev => {
      if (prev.includes(shapeId)) {
        return prev.filter(id => id !== shapeId);
      } else {
        return [...prev, shapeId];
      }
    });
  };

  const getSelectedShape = () => {
    if (!selectedId) return null;
    return shapes.find(shape => shape.id === selectedId);
  };
  
  const getSelectedShapes = () => {
    return shapes.filter(shape => selectedIds.includes(shape.id));
  };

  const isShapeSelected = (shapeId) => {
    return selectedIds.includes(shapeId);
  };
  
  // Grouping operations
  const groupShapes = async (shapeIds) => {
    if (!currentUser) {
      console.error('Cannot group shapes: No authenticated user');
      return false;
    }

    if (!shapeIds || shapeIds.length < 2) {
      console.warn('Need at least 2 shapes to create a group');
      return false;
    }

    try {
      setLoading('bulkOperations', true);

      // Generate unique group ID
      const groupId = crypto.randomUUID 
        ? crypto.randomUUID() 
        : `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      console.log(`📦 Creating group ${groupId} with ${shapeIds.length} shape(s)`);

      // Update all shapes with the group ID
      for (const shapeId of shapeIds) {
        await updateShapeInFirestore(shapeId, { groupId }, currentUser.uid);
      }

      console.log('✅ Shapes grouped successfully');
      return { success: true, groupId };
    } catch (error) {
      console.error('❌ Grouping failed:', error);
      return { success: false, error };
    } finally {
      setLoading('bulkOperations', false);
    }
  };

  const ungroupShapes = async (groupIdOrShapeIds) => {
    if (!currentUser) {
      console.error('Cannot ungroup shapes: No authenticated user');
      return false;
    }

    try {
      setLoading('bulkOperations', true);

      let shapesToUngroup = [];

      // If it's an array, treat as shape IDs
      if (Array.isArray(groupIdOrShapeIds)) {
        shapesToUngroup = groupIdOrShapeIds;
      } else {
        // Otherwise, it's a group ID - find all shapes with this group ID
        shapesToUngroup = shapes
          .filter(s => s.groupId === groupIdOrShapeIds)
          .map(s => s.id);
      }

      if (shapesToUngroup.length === 0) {
        console.warn('No shapes to ungroup');
        return false;
      }

      console.log(`📦 Ungrouping ${shapesToUngroup.length} shape(s)`);

      // Remove group ID from all shapes
      for (const shapeId of shapesToUngroup) {
        await updateShapeInFirestore(shapeId, { groupId: null }, currentUser.uid);
      }

      console.log('✅ Shapes ungrouped successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Ungrouping failed:', error);
      return { success: false, error };
    } finally {
      setLoading('bulkOperations', false);
    }
  };

  // Get all shapes in a group
  const getShapesInGroup = (groupId) => {
    if (!groupId) return [];
    return shapes.filter(s => s.groupId === groupId);
  };

  // Get group ID from a shape
  const getGroupId = (shapeId) => {
    const shape = shapes.find(s => s.id === shapeId);
    return shape?.groupId || null;
  };

  // When clicking a grouped shape, select entire group
  const selectShapeWithGroup = (shapeId) => {
    const shape = shapes.find(s => s.id === shapeId);
    if (!shape) {
      selectShape(shapeId);
      return;
    }

    if (shape.groupId) {
      // Select all shapes in the group
      const groupShapes = getShapesInGroup(shape.groupId);
      selectMultiple(groupShapes.map(s => s.id));
    } else {
      // Select just this shape
      selectShape(shapeId);
    }
  };

  // Context value
  const value = {
    // State
    shapes,
    selectedId,
    selectedIds,
    stageRef,
    loading,
    previewShapes,

    // State setters
    setSelectedIds,
    setPreviewShapes,

    // Shape operations
    addShape,
    updateShape,
    deleteShape,
    deleteAllShapes,
    getViewportCenter,

    // AI shape operations
    addAIShapes,

    // Selection methods
    clearSelection,
    selectShape,
    selectMultiple,
    toggleSelection,
    getSelectedShape,
    getSelectedShapes,
    isShapeSelected,
    
    // Grouping operations
    groupShapes,
    ungroupShapes,
    getShapesInGroup,
    getGroupId,
    selectShapeWithGroup
  };

  return (
    <CanvasContext.Provider value={value}>
      {children}
    </CanvasContext.Provider>
  );
}

export default CanvasContext;

